// Markdown renderer for conversations — renders readable, warm text, not code-like output.
// Handles: headings, bold, italic, inline code, code blocks, lists, blockquotes, links, tables, hr.
// Optional: enableTimestamps turns M:SS / M:SS-M:SS patterns into <button class="agora-ts">
// for click-to-seek wiring by parent components (e.g. PostCard + AgoraAudioPlayer).

export default function MarkdownRenderer({
  content,
  enableTimestamps = false,
}: {
  content: string
  enableTimestamps?: boolean
}) {
  const html = renderMarkdown(content, { enableTimestamps })
  return <div dangerouslySetInnerHTML={{ __html: html }} />
}

interface RenderOpts {
  enableTimestamps: boolean
}

function stripHtmlToMarkdown(text: string): string {
  let s = text
  s = s.replace(/<a\s+[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
  s = s.replace(/<a\s+[^>]*href=([^\s>"']+)[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
  s = s.replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
  s = s.replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
  s = s.replace(/<em>([\s\S]*?)<\/em>/gi, '*$1*')
  s = s.replace(/<i>([\s\S]*?)<\/i>/gi, '*$1*')
  s = s.replace(/<code>([\s\S]*?)<\/code>/gi, '`$1`')
  s = s.replace(/<br\s*\/?>/gi, '\n')
  s = s.replace(/<p>([\s\S]*?)<\/p>/gi, '$1\n\n')
  s = s.replace(/<[^>]+>/g, '')
  return s
}

function renderMarkdown(text: string, opts: RenderOpts): string {
  // Strip any HTML tags in message content, converting common tags to markdown equivalents
  text = /<[a-z][^>]*>/i.test(text) ? stripHtmlToMarkdown(text) : text

  // Handle code blocks first (before any other processing)
  const codeBlocks: string[] = []
  let processed = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_m, lang, code) => {
    const idx = codeBlocks.length
    const langLabel = lang ? `<span style="position:absolute;top:6px;right:10px;font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.5px">${esc(lang)}</span>` : ''
    codeBlocks.push(
      `<div style="position:relative;margin:8px 0;background:var(--bg-elevated);border:1px solid var(--border);border-radius:8px;overflow:hidden">${langLabel}<pre style="margin:0;padding:12px 14px;overflow-x:auto;font-size:13px;line-height:1.5;font-family:'SF Mono',Menlo,Consolas,monospace;color:var(--text-primary);white-space:pre-wrap;word-break:break-all"><code>${esc(code.trim())}</code></pre></div>`
    )
    return `\x00CB${idx}\x00`
  })

  return processed
    .split('\n\n')
    .map(block => {
      // Restore code blocks
      if (block.match(/\x00CB\d+\x00/)) {
        return block.replace(/\x00CB(\d+)\x00/g, (_m, idx) => codeBlocks[parseInt(idx)] || '')
      }

      // Horizontal rule
      if (block.match(/^---+$/m)) return '<hr style="border:none;border-top:1px solid var(--border);margin:12px 0">'

      // Headers
      if (block.startsWith('### ')) return `<h4 style="font-size:14px;font-weight:600;margin:14px 0 4px;color:var(--text-primary)">${inline(block.slice(4), opts)}</h4>`
      if (block.startsWith('## ')) return `<h3 style="font-size:15px;font-weight:600;margin:16px 0 6px;color:var(--text-primary)">${inline(block.slice(3), opts)}</h3>`
      if (block.startsWith('# ')) return `<h2 style="font-size:16px;font-weight:600;margin:16px 0 8px;color:var(--text-primary)">${inline(block.slice(2), opts)}</h2>`

      // Blockquote
      if (block.startsWith('> ')) {
        const lines = block.split('\n').map(l => l.replace(/^>\s?/, '')).join('<br>')
        return `<blockquote style="border-left:3px solid var(--border-accent);padding:4px 0 4px 14px;margin:8px 0;color:var(--text-secondary);font-style:italic">${inline(lines, opts)}</blockquote>`
      }

      // Unordered list
      if (block.match(/^[-*] /m)) {
        const items = block.split('\n').filter(l => l.match(/^[-*] /)).map(l =>
          `<li style="margin-bottom:3px">${inline(l.slice(2), opts)}</li>`
        )
        return `<ul style="padding-left:20px;margin:6px 0;list-style-type:disc;color:var(--text-primary)">${items.join('')}</ul>`
      }

      // Ordered list
      if (block.match(/^\d+\. /m)) {
        const items = block.split('\n').filter(l => l.match(/^\d+\. /)).map(l =>
          `<li style="margin-bottom:3px">${inline(l.replace(/^\d+\.\s/, ''), opts)}</li>`
        )
        return `<ol style="padding-left:20px;margin:6px 0;color:var(--text-primary)">${items.join('')}</ol>`
      }

      // Table
      if (block.includes('|') && block.includes('\n')) {
        const rows = block.split('\n').filter(r => r.trim().startsWith('|'))
        if (rows.length >= 2) {
          const headerCells = rows[0].split('|').filter(Boolean).map(c => c.trim())
          const isSeparator = (r: string) => r.replace(/[|\s-:]/g, '') === ''
          const dataRows = rows.filter((r, i) => i > 0 && !isSeparator(r))
          const ths = headerCells.map(c => `<th style="padding:6px 10px;text-align:left;font-size:12px;font-weight:600;color:var(--text-secondary);border-bottom:2px solid var(--border)">${inline(c, opts)}</th>`).join('')
          const trs = dataRows.map(r => {
            const cells = r.split('|').filter(Boolean).map(c => c.trim())
            return `<tr>${cells.map(c => `<td style="padding:5px 10px;font-size:13px;border-bottom:1px solid var(--border);color:var(--text-primary)">${inline(c, opts)}</td>`).join('')}</tr>`
          }).join('')
          return `<table style="width:100%;border-collapse:collapse;margin:8px 0"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`
        }
      }

      // Paragraph — handle single newlines within blocks
      const lines = block.split('\n').map(l => inline(l, opts)).join('<br>')
      return `<p style="margin:4px 0;color:var(--text-primary)">${lines}</p>`
    })
    .join('')
}

function inline(text: string, opts: RenderOpts): string {
  let s = esc(text)
  // Restore code block placeholders that might be inline
  s = s.replace(/\x00CB(\d+)\x00/g, '[code block]')
  // Bold
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:600;color:var(--text-primary)">$1</strong>')
  // Italic
  s = s.replace(/\*(.+?)\*/g, '<em>$1</em>')
  // Inline code
  s = s.replace(/`(.+?)`/g, '<code style="padding:1px 5px;border-radius:4px;font-size:0.9em;font-family:\'SF Mono\',Menlo,Consolas,monospace;background:var(--bg-elevated);color:var(--text-primary)">$1</code>')
  // Images
  s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, url) => {
    const safeUrl = isSafeUrl(url) ? url : ''
    return safeUrl ? `<img src="${safeUrl}" alt="${alt}" style="max-width:100%;border-radius:8px;margin:6px 0;display:block" loading="lazy">` : alt
  })
  // Links
  s = s.replace(/\[(.+?)\]\((.+?)\)/g, (_m, label, url) => {
    const safeUrl = isSafeUrl(url) ? url : '#'
    return `<a href="${safeUrl}" style="color:#58a6ff;text-decoration:none" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'" target="_blank" rel="noopener noreferrer">${label}</a>`
  })
  // Bare URLs (after markdown links so we don't double-link)
  s = s.replace(/(?<!["=])(https?:\/\/[^\s<)]+?)(?=[.,;:!?)]*(?:\s|$))/g, (url) => {
    return `<a href="${url}" style="color:#58a6ff;text-decoration:none" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'" target="_blank" rel="noopener noreferrer">${url}</a>`
  })
  // Hashtags
  s = s.replace(/(#[\w/-]+)/g, '<span style="color:#58a6ff">$1</span>')
  // Timestamp cues (opt-in): M:SS or M:SS-M:SS / M:SS–M:SS / M:SS—M:SS.
  // Emit data attributes; click wiring is the parent's job.
  if (opts.enableTimestamps) {
    s = s.replace(
      /\b(\d{1,2}):([0-5]\d)(?:\s*[-–—]\s*(\d{1,2}):([0-5]\d))?\b/g,
      (m, m1, s1, m2, s2) => {
        const start = parseInt(m1, 10) * 60 + parseInt(s1, 10)
        const endAttr =
          m2 != null
            ? ` data-end="${parseInt(m2, 10) * 60 + parseInt(s2, 10)}"`
            : ''
        return `<button type="button" class="agora-ts" data-start="${start}"${endAttr} style="background:transparent;border:1px solid #856FC855;color:#B294DD;font:inherit;padding:0 6px;border-radius:6px;cursor:pointer;font-variant-numeric:tabular-nums">${m}</button>`
      },
    )
  }
  return s
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('mailto:')) return true
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('./') || trimmed.startsWith('../')) return true
  return !trimmed.includes(':')
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
